import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  Box, Typography, InputAdornment, TextField, MenuItem,
  IconButton, CircularProgress, Chip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { components } from 'cng-web-lib'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import { PARTY_TYPE } from '../constants/entityOptions'
import pathMap from 'src/paths/pathMap'
import { useHistory } from 'react-router-dom'

const { button: { CngButton } } = components

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 12,
      maxWidth: 680,
      width: '100%'
    }
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  titleText: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    fontSize: 15
  },
  infoBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    background: '#f0f7ff',
    border: '1px solid #b3d4f5',
    borderRadius: 8,
    marginBottom: 12
  },
  searchBar: {
    marginBottom: 8
  },
  typeFilter: {
    marginBottom: 14
  },
  partyList: {
    maxHeight: 380,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 0
  },
  partyRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:hover': {
      background: theme.palette.action.hover
    }
  },
  partyName: {
    fontWeight: 600,
    fontSize: 13,
    marginBottom: 3
  },
  partyMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap'
  },
  partyTypeChip: {
    fontSize: 11,
    height: 20,
    background: '#e8edf2',
    color: '#374151'
  },
  partyId: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontFamily: 'monospace'
  },
  partyLocation: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: theme.palette.text.secondary
  },
  footer: {
    padding: '12px 20px',
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  footerNote: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  footerActions: {
    display: 'flex',
    gap: 8
  }
}))

function getPartyTypeLabel(code) {
  const found = PARTY_TYPE.find(p => p.value === code)
  return found ? found.text : code
}

/**
 * PartyMasterPickerDialog
 *
 * Props:
 *   open        {boolean}  - dialog open state
 *   onClose     {fn}       - called when dialog is dismissed
 *   onSelect    {fn}       - called with the selected party object
 */
function PartyMasterPickerDialog({ open, onClose, onSelect }) {
  const classes = useStyles()
  const history = useHistory()

  const [parties, setParties] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [warnOpen, setWarnOpen] = useState(false)

  function handleManageClick() {
    setWarnOpen(true)
  }

  function handleWarnConfirm() {
    setWarnOpen(false)
    onClose && onClose()
    history.push(pathMap.UAE_MPCI_PARTY_MASTER_LIST_VIEW)
  }

  const fetchParties = useCallback(() => {
    setLoading(true)
    fetch(UaeMpciApiUrls.PARTY_MASTER_LIST, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
      body: JSON.stringify({ page: 0, size: 200, sortBy: 'partyName', sortDir: 'ASC' })
    })
      .then((res) => res.json())
            .then((json) => {
        const data =
          json?.data?.records?.content ??
          json?.data?.content ??
          json?.data ??
          []
        setParties(Array.isArray(data) ? data : [])
        setLoading(false)
      })
      .catch(() => {
        setParties([])
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (open) {
      setSearchText('')
      setTypeFilter('ALL')
      fetchParties()
    }
  }, [open, fetchParties])

  const filtered = parties.filter((p) => {
    const matchType = typeFilter === 'ALL' || p.partyType === typeFilter
    const q = searchText.toLowerCase()
    const matchSearch =
      !q ||
      (p.partyName || '').toLowerCase().includes(q) ||
      (p.partyId || '').toLowerCase().includes(q) ||
      (p.partyType || '').toLowerCase().includes(q) ||
      (p.city || '').toLowerCase().includes(q)
    return matchType && matchSearch
  })

  function handleSelect(party) {
    onSelect && onSelect(party)
    onClose && onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} maxWidth='md'>
      {/* Title */}
      <Box className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          <FontAwesomeIcon icon={['fal', 'address-book']} style={{ fontSize: 15, opacity: 0.7 }} />
          Pick from Party Master
        </Typography>
        <IconButton size='small' onClick={onClose}>
          <FontAwesomeIcon icon={['fal', 'xmark']} style={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box style={{ padding: '16px 20px', overflowY: 'auto' }}>
        {/* Info banner */}
        <Box className={classes.infoBanner}>
          <FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 13, flexShrink: 0, color: '#2563eb' }} />
          <Typography variant='caption' style={{ color: '#1e40af' }}>
            Select a party from your master list to auto-fill this section. You can still edit any field afterward.
          </Typography>
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          size='small'
          variant='outlined'
          placeholder='Search by Party Name, TRN, Type, City...'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className={classes.searchBar}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <FontAwesomeIcon icon={['fal', 'magnifying-glass']} style={{ fontSize: 13, opacity: 0.5 }} />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position='end'>
                <IconButton size='small' onClick={() => setSearchText('')}>
                  <FontAwesomeIcon icon={['fal', 'xmark']} style={{ fontSize: 12 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        {/* Type filter */}
        <TextField
          select
          fullWidth
          size='small'
          variant='outlined'
          className={classes.typeFilter}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <FontAwesomeIcon icon={['fal', 'tag']} style={{ fontSize: 12, opacity: 0.5 }} />
                <Typography variant='caption' style={{ marginLeft: 4, marginRight: 4, color: '#6b7280' }}>Type:</Typography>
              </InputAdornment>
            )
          }}
        >
          <MenuItem value='ALL'>All</MenuItem>
          {PARTY_TYPE.map(pt => (
            <MenuItem key={pt.value} value={pt.value}>{pt.text}</MenuItem>
          ))}
        </TextField>

        {/* Party list */}
        <Box border={1} borderColor='divider' borderRadius={8} style={{ overflow: 'hidden' }}>
          {loading ? (
            <Box display='flex' alignItems='center' justifyContent='center' style={{ padding: 40 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box className={classes.emptyState}>
              <FontAwesomeIcon icon={['fal', 'inbox']} style={{ fontSize: 28, marginBottom: 8, opacity: 0.3 }} />
              <Typography variant='body2'>No parties found</Typography>
              <Typography variant='caption'>Try adjusting your search or add a new party to your master list.</Typography>
            </Box>
          ) : (
            <Box className={classes.partyList}>
              {filtered.map((party) => (
                <Box key={party.id} className={classes.partyRow}>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography className={classes.partyName}>{party.partyName}</Typography>
                    <Box className={classes.partyMeta}>
                      <Chip
                        label={getPartyTypeLabel(party.partyType)}
                        size='small'
                        className={classes.partyTypeChip}
                      />
                      {party.partyId && (
                        <span className={classes.partyId}>{party.partyId}</span>
                      )}
                      {(party.city || party.country) && (
                        <span className={classes.partyLocation}>
                          <FontAwesomeIcon icon={['fal', 'location-dot']} style={{ fontSize: 10 }} />
                          {[party.city, party.country].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </Box>
                  </Box>
                  <CngButton
                    color='primary'
                    size='small'
                    onClick={() => handleSelect(party)}
                    startIcon={<FontAwesomeIcon icon={['fal', 'check']} />}
                  >
                    Select
                  </CngButton>
                </Box>
              ))}
            </Box>
          )}
                </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.footer}>
        <Typography className={classes.footerNote}>
          <FontAwesomeIcon icon={['fal', 'circle-info']} style={{ fontSize: 11 }} />
          Showing parties from your{' '}
          <span
            style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleManageClick}
          >
            Party Master
          </span>
        </Typography>
        <Box className={classes.footerActions}>
          <CngButton color='secondary' size='small' onClick={onClose}
            startIcon={<FontAwesomeIcon icon={['fal', 'xmark']} />}>
            Cancel
          </CngButton>
          <CngButton color='primary' size='small'
            onClick={handleManageClick}
            startIcon={<FontAwesomeIcon icon={['fal', 'arrow-up-right-from-square']} />}>
            Manage Party Master
          </CngButton>
        </Box>
      </Box>
      {/* Unsaved draft warning */}
      <Dialog open={warnOpen} onClose={() => setWarnOpen(false)} maxWidth='xs' fullWidth>
        <Box style={{ padding: '20px 24px' }}>
          <Box display='flex' alignItems='center' style={{ gap: 10, marginBottom: 12 }}>
            <FontAwesomeIcon icon={['fal', 'triangle-exclamation']} style={{ fontSize: 20, color: '#d97706' }} />
            <Typography style={{ fontWeight: 600, fontSize: 15 }}>Unsaved changes may be lost</Typography>
          </Box>
          <Typography variant='body2' style={{ color: '#4b5563', marginBottom: 20 }}>
            You are about to leave this page. Any unsaved draft changes will be lost. Please save your draft before proceeding.
          </Typography>
          <Box display='flex' justifyContent='flex-end' style={{ gap: 8 }}>
            <CngButton color='secondary' size='small' onClick={() => setWarnOpen(false)}
              startIcon={<FontAwesomeIcon icon={['fal', 'arrow-left']} />}>
              Stay on Page
            </CngButton>
            <CngButton color='primary' size='small' onClick={handleWarnConfirm}
              startIcon={<FontAwesomeIcon icon={['fal', 'arrow-up-right-from-square']} />}>
              Leave Anyway
            </CngButton>
          </Box>
        </Box>
      </Dialog>
    </Dialog>
  )
}

export default PartyMasterPickerDialog
